import { create } from "zustand";
import { optimizeBase } from "@/optimizer/optimize";
import { generateBasePhases } from "@/optimizer/phases";
import type {
  AdditionalTarget,
  BasePhasePlan,
  OptimizedBaseResult,
  OptimizerInput,
} from "@/optimizer/types";
import { prosperousProvider } from "@/provider/fio-provider";
import type { ObjectiveType, TargetPeriod } from "@/schema/types";

/**
 * The optimizer's form and its latest result, kept outside the screen.
 *
 * A screen's own state is thrown away when you navigate off it, which meant
 * leaving the optimizer to check a planet lost both the inputs and a plan that
 * had taken a minute to solve. Held here instead, they survive navigation, and
 * a solve that is still running when you leave lands its result anyway.
 *
 * This is working state, not data: it lives for the session and is not written
 * to the save file. Saving a plan as a template is the deliberate way to keep one.
 */

export type OptimizerForm = {
  /** FIO natural id or name. */
  planetCode: string;
  /** A base already in the scenario, or null when planning for somewhere new. */
  selectedPlanetId: string | null;
  /** A new base needs a core module. An existing one already has its. */
  isNewBase: boolean;
  availableArea: number;
  objectiveType: ObjectiveType;
  targetProduct: string;
  targetAmount: number | "";
  targetPeriod: TargetPeriod;
  additionalTargets: AdditionalTarget[];
  inHouse: string[];
  /** Product ticker to chosen recipe id, for recipes the solver got wrong. */
  recipeOverrides: Record<string, string>;
};

type OptimizerState = OptimizerForm & {
  result: OptimizedBaseResult | null;
  phases: BasePhasePlan | null;
  running: boolean;
  phasing: boolean;
  error: string | null;
  sentToList: string | null;

  set: (patch: Partial<OptimizerForm>) => void;
  run: (overrides?: Record<string, string>) => Promise<void>;
  generatePhases: () => Promise<void>;
  setSentToList: (name: string | null) => void;
  reset: () => void;
};

const INITIAL_FORM: OptimizerForm = {
  planetCode: "",
  selectedPlanetId: null,
  isNewBase: true,
  availableArea: 500,
  objectiveType: "MAXIMIZE_OUTPUT",
  targetProduct: "",
  targetAmount: "",
  targetPeriod: "WEEK",
  additionalTargets: [],
  inHouse: ["DW", "RAT"],
  recipeOverrides: {},
};

const INITIAL_RESULTS = {
  result: null,
  phases: null,
  running: false,
  phasing: false,
  error: null,
  sentToList: null,
};

export function toOptimizerInput(form: OptimizerForm, overrides = form.recipeOverrides): OptimizerInput {
  return {
    planetCode: form.planetCode.trim().toUpperCase(),
    availableArea: form.availableArea,
    objectiveType: form.objectiveType,
    targetProduct: form.targetProduct.trim().toUpperCase(),
    targetAmount: form.targetAmount === "" ? null : form.targetAmount,
    targetPeriod: form.objectiveType === "CLOSEST_TO_TARGET" ? form.targetPeriod : null,
    additionalTargets: form.additionalTargets.filter((entry) => entry.product.trim() && entry.amount > 0),
    includeCoreModule: form.isNewBase,
    selectedWorkforceInHouseResources: form.inHouse,
    selectedRecipeOverrides: overrides,
    excludedRecipes: [],
  };
}

// A solve that finishes after a newer one was started is out of date, and
// letting it land would replace the plan the user is actually looking at.
let latestRun = 0;

export const useOptimizerStore = create<OptimizerState>((set, get) => ({
  ...INITIAL_FORM,
  ...INITIAL_RESULTS,

  set: (patch) => set(patch),

  async run(overrides) {
    const runId = ++latestRun;
    // Stages are solved from the same inputs, so a new plan makes them stale.
    set({ running: true, error: null, phases: null });

    try {
      const output = await optimizeBase(toOptimizerInput(get(), overrides ?? get().recipeOverrides), prosperousProvider);
      if (runId === latestRun) set({ result: output, running: false });
    } catch (caught) {
      if (runId === latestRun) {
        set({ result: null, running: false, error: caught instanceof Error ? caught.message : String(caught) });
      }
    }
  },

  async generatePhases() {
    set({ phasing: true, error: null });
    try {
      const phases = await generateBasePhases(toOptimizerInput(get()), prosperousProvider);
      set({ phases, phasing: false });
    } catch (caught) {
      set({ phasing: false, error: caught instanceof Error ? caught.message : String(caught) });
    }
  },

  setSentToList: (name) => set({ sentToList: name }),

  reset() {
    latestRun += 1;
    set({ ...INITIAL_FORM, ...INITIAL_RESULTS });
  },
}));
